import React, { useEffect } from 'react';
import { useFormStore, useNavigation } from './store/formStore';
import { ProgressBar } from './components/form/ProgressBar';
import { calculateProgress } from './utils/navigation';
import { SubmitLoadingOverlay } from './components/ui/SubmitLoadingOverlay';

// Import des étapes
import { WelcomeStep } from './steps/WelcomeStep';
import { SimulationStep } from './steps/SimulationStep';
import { DurationStep } from './steps/DurationStep';
import { PersonalInfoStep } from './steps/PersonalInfoStep';
import { PersonalSituationStep } from './steps/PersonalSituationStep';
import { AddressStep } from './steps/AddressStep';
import { CoBorrowerStep } from './steps/CoBorrowerStep';
import { InsuranceStep } from './steps/InsuranceStep';
import { SummaryStep } from './steps/SummaryStep';
import { SuccessStep } from './steps/SuccessStep';

function App() {
  const { formData, completedSteps, visitedSteps, errors, isSubmitting } = useFormStore();
  const { currentStepId, goToStep } = useNavigation();

  // Scroll vers le haut à chaque changement d'étape
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
  }, [currentStepId]);

  // Scroll vers le haut quand le loading de soumission commence
  useEffect(() => {
    if (isSubmitting) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.parent.postMessage({ type: 'scrollToTop' }, '*');
    }
  }, [isSubmitting]);

  // Configuration des étapes
  const stepComponents: Record<string, React.ComponentType> = {
    'welcome': WelcomeStep,
    'simulation': SimulationStep,
    'duration': DurationStep,
    'personal_info': PersonalInfoStep,
    'personal_situation': PersonalSituationStep,
    'address': AddressStep,
    'co_borrower': CoBorrowerStep,
    'insurance': InsuranceStep,
    'summary': SummaryStep,
    'success': SuccessStep
  };

  // Déterminer les étapes visibles selon la présence d'un co-emprunteur
  const hasCoBorrower = formData.hasCoBorrower === true;

  const allStepLabels = [
    'Accueil',
    'Projet',
    'Durée',
    'Emprunteur',
    'Co-emprunteur',  // Index 4
    'Situation',
    'Adresse',
    'Assurance',
    'Résumé'
  ];

  // Filtrer les labels selon la présence du co-emprunteur
  const stepLabels = hasCoBorrower
    ? allStepLabels
    : allStepLabels.filter((_, index) => index !== 4); // Retirer "Co-emprunteur" à l'index 4

  // Obtenir le composant actuel
  const CurrentStepComponent = stepComponents[currentStepId];
  
  if (!CurrentStepComponent) {
    return (
      <div className="bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-error-600 mb-4">Erreur</h1>
          <p className="text-neutral-700">Étape non trouvée : {currentStepId}</p>
        </div>
      </div>
    );
  }
  
  // Calculer la progression
  const { currentStepIndex, totalSteps } = calculateProgress(
    currentStepId,
    completedSteps,
    formData
  );
  
  // Gestion du clic sur une étape dans la barre de progression
  const handleStepClick = (stepNumber: number) => {
    let steps = ['welcome', 'simulation', 'duration', 'personal_info'];

    // Ajouter co_borrower si nécessaire
    if (hasCoBorrower) {
      steps.push('co_borrower');
    }

    steps.push('personal_situation', 'address', 'insurance', 'summary');

    const targetStepId = steps[stepNumber - 1];

    if (targetStepId && (stepNumber <= currentStepIndex || completedSteps.includes(targetStepId) || visitedSteps.includes(targetStepId))) {
      goToStep(targetStepId);
    }
  };
  
  return (
    <div className="bg-neutral-50">
      {isSubmitting && <SubmitLoadingOverlay />}
      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Progress bar (hidden on welcome and success pages) */}
        {currentStepId !== 'welcome' && currentStepId !== 'success' && (
          <div className="mb-8">
            <ProgressBar
              currentStep={currentStepIndex}
              totalSteps={totalSteps}
              completedSteps={completedSteps.map(id => {
                let steps = ['welcome', 'simulation', 'duration', 'personal_info'];
                if (hasCoBorrower) {
                  steps.push('co_borrower');
                }
                steps.push('personal_situation', 'address', 'insurance', 'summary');
                const index = steps.indexOf(id);
                return index >= 0 ? index + 1 : 0;
              }).filter(num => num > 0)}
              accessibleSteps={(visitedSteps ?? []).map(id => {
                let steps = ['welcome', 'simulation', 'duration', 'personal_info'];
                if (hasCoBorrower) {
                  steps.push('co_borrower');
                }
                steps.push('personal_situation', 'address', 'insurance', 'summary');
                const index = steps.indexOf(id);
                return index >= 0 ? index + 1 : 0;
              }).filter(num => num > 0)}
              stepLabels={stepLabels}
              onStepClick={handleStepClick}
            />
          </div>
        )}

        {/* Global error display */}
        {errors.submit && (
          <div className="mb-6 max-w-2xl mx-auto">
            <div className="alert-error">
              <h3 className="font-medium mb-1">Une erreur est survenue</h3>
              <p>Votre demande n'a pas pu être envoyée. Nos équipes sont averties et font tout pour corriger cela rapidement.</p>
            </div>
          </div>
        )}
        
        {/* Étape courante */}
        <div className="transition-all duration-300 ease-in-out">
          <CurrentStepComponent />
        </div>
      </main>
    </div>
  );
}

export default App;